import { scaleLinear } from "d3-scale";
import React, { useRef, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

import "./stylesGeographicUserInsights.css";
import { Skeleton } from "antd";
import { formatNumberWithCommas, formatCurrencyWithCommas } from "../../../../utils/NumberFormatter";

const url = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
const geoUrl = url;

const GeographicUserInsights = ({
  data = [],
  title = null,
  isDollars = false,
  isApiRunning = false,
}) => {
  const containerRef = useRef(null);
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const colorScale = scaleLinear()
    .domain([0, Math.max(...data.map((d) => d.amount))])
    .range(["#dfedfe", "#28388a"]);

  const getColor = (name, darker = false) => {
    const state = data.find((d) => d.name === name);
    if (!state) return "#2f2f39";
    return state
      ? darker
        ? colorScale(state.amount * 1.1)
        : colorScale(state.amount)
      : "#EEE";
  };

  const handleMouseEnter = (geo, evt) => {
    const name = geo.properties.name;
    const state = data.find((d) => d.name === name);
    const rect = containerRef.current.getBoundingClientRect();

    let formattedAmount = "No data";
    if (state?.amount !== undefined && state?.amount !== null) {
      if (isDollars) {
        formattedAmount = formatCurrencyWithCommas(state.amount);
      } else {
        formattedAmount = formatNumberWithCommas(state.amount, 0);
      }
    }

    setTooltipContent(`${name}: ${formattedAmount}`);
    setTooltipPosition({
      x: evt.clientX - rect.left,
      y: evt.clientY - rect.top,
    });
  };

  const handleMouseLeave = () => {
    setTooltipContent("");
  };

  const getTheBiggestValue = (data) => {
    if (data.length === 0) return 0;
    let max = Math.max(...data.map((d) => d.amount));
    
    if (isNaN(max)) return 0;
    return max;
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <div className="geographic-user-insights-header">
        {title && <h3>{title}</h3>}
      </div>
      {isApiRunning && <Skeleton active />}
      {!isApiRunning && (
        <>
          <ComposableMap
            projection="geoAlbersUsa"
            style={{ height: "100%", width: "100%" }}
          >
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const name = geo.properties.name;
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                      onMouseLeave={handleMouseLeave}
                      style={{
                        default: {
                          fill: getColor(name),
                          stroke: "#000",
                          strokeWidth: 0.3,
                          outline: "none",
                          cursor: "pointer",
                        },
                        hover: {
                          fill: getColor(name, true),
                          outline: "none",
                          cursor: "pointer",
                        },
                        pressed: { fill: "#E42", outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>
          <div className="geographic-user-insights-info">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span>
                {isDollars
                  ? Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(getTheBiggestValue(data))
                  : getTheBiggestValue(data)}
              </span>
              <div className="geographic-user-insights-info-blocks">
                <div
                  className="geographic-user-insights-info-block"
                  style={{ backgroundColor: "#28388A" }}
                ></div>
                <div
                  className="geographic-user-insights-info-block"
                  style={{ backgroundColor: "#3C4EA9" }}
                ></div>
                <div
                  className="geographic-user-insights-info-block"
                  style={{ backgroundColor: "#4F62C9" }}
                ></div>
                <div
                  className="geographic-user-insights-info-block"
                  style={{ backgroundColor: "#6177E8" }}
                ></div>
                <div
                  className="geographic-user-insights-info-block"
                  style={{ backgroundColor: "#7A9BEF" }}
                ></div>
                <div
                  className="geographic-user-insights-info-block"
                  style={{ backgroundColor: "#92BFF5" }}
                ></div>
                <div
                  className="geographic-user-insights-info-block"
                  style={{ backgroundColor: "#C2DAF5" }}
                ></div>
                <div
                  className="geographic-user-insights-info-block"
                  style={{ backgroundColor: "#DFEDFE" }}
                ></div>
              </div>
              <span>0</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div className="geographic-user-insights-info-blocks">
                <div
                  className="geographic-user-insights-info-block"
                  style={{ backgroundColor: "#2F2F39" }}
                ></div>
              </div>
              <span>Unavailable</span>
            </div>
          </div>
          {tooltipContent && (
            <div
              ref={(el) => {
                if (el) {
                  const tooltipWidth = el.offsetWidth;
                  const tooltipHeight = el.offsetHeight;
                  const rect = containerRef.current.getBoundingClientRect();

                  // Coordenadas absolutas do tooltip (na tela)
                  const absLeft = tooltipPosition.x + rect.left + 10;
                  const absTop = tooltipPosition.y + rect.top + 10;

                  // Se sair da direita da tela, ajusta para a esquerda
                  if (absLeft + tooltipWidth > window.innerWidth) {
                    el.style.left = `${
                      tooltipPosition.x - tooltipWidth - 10
                    }px`;
                  } else {
                    el.style.left = `${tooltipPosition.x + 10}px`;
                  }

                  // Se sair da parte inferior, ajusta para cima
                  if (absTop + tooltipHeight > window.innerHeight) {
                    el.style.top = `${
                      tooltipPosition.y - tooltipHeight - 10
                    }px`;
                  } else {
                    el.style.top = `${tooltipPosition.y + 10}px`;
                  }
                }
              }}
              style={{
                position: "absolute",
                background: "#25252F",
                color: "#fff",
                borderRadius: "4px",
                padding: "10px",
                pointerEvents: "none",
                zIndex: 1000,
                whiteSpace: "nowrap",
              }}
            >
              {tooltipContent}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GeographicUserInsights;
